import { useState } from 'react';
import type { ToolLogEntry } from './useAgentChat.ts';
import PanelEmpty from './PanelEmpty.tsx';
import './ToolLog.css';

interface ToolLogProps {
  entries: ToolLogEntry[];
}

/** Inspector "Tools" section: a reverse-chronological log of the agent's tool
 *  calls. Rows flip from "running" to "ok" as calls settle, and each row
 *  expands to reveal the full request input and result output. The section
 *  header (and its call count) lives in the inspector accordion, so this
 *  renders body content only. */
export default function ToolLog({ entries }: ToolLogProps) {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());

  const toggle = (id: number): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (entries.length === 0) {
    return (
      <section className="om-toollog">
        <PanelEmpty>Tools the agent uses will show up here as you chat.</PanelEmpty>
      </section>
    );
  }

  return (
    <section className="om-toollog">
      <div className="om-toollog__list">
        {entries.map((entry) => {
          const isOpen = expanded.has(entry.id);
          return (
            <div
              key={entry.id}
              className={`om-tlrow${isOpen ? ' om-tlrow--open' : ''}`}
            >
              <button
                type="button"
                className="om-tlrow__toggle"
                aria-expanded={isOpen}
                onClick={() => toggle(entry.id)}
              >
                <div className="om-tlrow__ic">⚙</div>
                <div className="om-tlrow__main">
                  <div className="om-tlrow__name">
                    {entry.name}
                    <span
                      className={`om-tlrow__badge om-tlrow__badge--${entry.running ? 'run' : 'ok'}`}
                    >
                      {entry.running ? 'running' : 'ok'}
                    </span>
                  </div>
                  <div className="om-tlrow__args">{entry.args}</div>
                </div>
                <div className="om-tlrow__time">{entry.stamp}</div>
                <span className="om-tlrow__chevron" aria-hidden="true">
                  {isOpen ? '▾' : '▸'}
                </span>
              </button>
              {isOpen ? (
                <div className="om-tlrow__detail">
                  <div className="om-tlrow__section">
                    <div className="om-tlrow__label">request</div>
                    <pre className="om-tlrow__code">{entry.request || '—'}</pre>
                  </div>
                  <div className="om-tlrow__section">
                    <div className="om-tlrow__label">result</div>
                    <pre className="om-tlrow__code">
                      {entry.running ? 'awaiting result…' : entry.result || '—'}
                    </pre>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
